import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import QRCodeStyling from 'qr-code-styling';

// Heart SVG path component
function HeartIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      width="24"
      height="24"
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

export interface DownloadOptions {
  fileName?: string;
  firstName?: string;
  lastName?: string;
}

export interface StyledQRCodeRef {
  download: (options?: DownloadOptions) => Promise<void>;
}

interface StyledQRCodeProps {
  value: string;
  size?: number;
  dotColor?: string;
  finderColor?: string;
  frameColor?: string;
  showHearts?: boolean;
  showFrame?: boolean;
}

const StyledQRCode = forwardRef<StyledQRCodeRef, StyledQRCodeProps>(({
  value,
  size = 200,
  dotColor = '#7C3AED',      // Purple/violet
  finderColor = '#EC4899',   // Pink
  frameColor = '#1E3A5A',    // Navy blue
  showHearts = true,
  showFrame = true,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);

  // Frame and padding dimensions
  const frameStroke = showFrame ? 3 : 0;
  const framePadding = showFrame ? 8 : 0;
  const heartOverflow = showHearts ? 42 : 0; // Extra space for hearts that overflow (increased to prevent clipping on download)
  const totalSize = size + (framePadding + frameStroke) * 2;
  const containerSize = totalSize + heartOverflow * 2;

  // Heart positions matching reference design (scales increased for bigger hearts)
  const heartPositions = useMemo(() => [
    // Top-right cluster (3 hearts) - distributed around the corner
    { x: totalSize - 10, y: 20, rotation: -10, scale: 1.7 },
    { x: totalSize - 6, y: -16, rotation: 20, scale: 1.4 },
    { x: totalSize - 36, y: -4, rotation: 30, scale: 1.2 },
    // Bottom-left cluster (2 hearts)
    { x: -12, y: totalSize - 40, rotation: -20, scale: 1.7 },
    { x: -8, y: totalSize + 6, rotation: 10, scale: 1.4 },
    // Bottom center (1 heart)
    { x: totalSize / 2 - 46, y: totalSize + 6, rotation: 0, scale: 1.2 },
  ], [totalSize]);

  useEffect(() => {
    const qrCode = new QRCodeStyling({
      width: size,
      height: size,
      type: 'canvas',
      data: value,
      dotsOptions: {
        color: dotColor,
        type: 'dots', // Circular dots
      },
      cornersSquareOptions: {
        color: finderColor,
        type: 'extra-rounded', // Rounded outer finder pattern
      },
      cornersDotOptions: {
        color: finderColor,
        type: 'dot', // Circular inner finder pattern dot
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      qrOptions: {
        errorCorrectionLevel: 'H',
      },
    });

    qrCodeRef.current = qrCode;

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      qrCode.append(containerRef.current);
    }

    return () => {
      qrCodeRef.current = null;
    };
  }, [value, size, dotColor, finderColor]);

  // Download function that creates a composite image with frame, hearts, and name
  const handleDownload = useCallback(async (options: DownloadOptions = {}) => {
    const { fileName = 'mypink-qrcode.png', firstName, lastName } = options;
    const canvas = containerRef.current?.querySelector('canvas');
    if (!canvas) return;

    // Calculate name height if provided
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const nameHeight = fullName ? 50 : 0; // Space for name above QR code
    const namePadding = fullName ? 16 : 0; // Padding below name

    // Measure text width to determine canvas width
    const nameFont = 'bold 28px Arial, Helvetica, sans-serif';
    let exportWidth = containerSize;
    if (fullName) {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        tempCtx.font = nameFont;
        const textWidth = tempCtx.measureText(fullName).width;
        exportWidth = Math.max(containerSize, textWidth + 48); // 48px padding for text
      }
    }

    // Create export canvas at 2x resolution for high DPI
    const scale = 2;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = exportWidth * scale;
    exportCanvas.height = (containerSize + nameHeight + namePadding) * scale;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(scale, scale);

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportWidth, containerSize + nameHeight + namePadding);

    // Draw name if provided
    if (fullName) {
      ctx.fillStyle = '#1f2937'; // gray-800
      ctx.font = nameFont;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(fullName, exportWidth / 2, nameHeight / 2 + 8);
    }

    // Calculate horizontal offset to center QR code in wider canvas
    const horizontalOffset = (exportWidth - containerSize) / 2;

    // Calculate offsets for centering (shift down by name height)
    const offset = heartOverflow;
    const verticalOffset = nameHeight + namePadding;

    // Draw navy frame if enabled
    if (showFrame) {
      ctx.strokeStyle = frameColor;
      ctx.lineWidth = frameStroke;
      ctx.beginPath();
      const frameRadius = 12;
      const x = horizontalOffset + offset;
      const y = offset + verticalOffset;
      const w = totalSize;
      const h = totalSize;
      ctx.moveTo(x + frameRadius, y);
      ctx.lineTo(x + w - frameRadius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + frameRadius);
      ctx.lineTo(x + w, y + h - frameRadius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - frameRadius, y + h);
      ctx.lineTo(x + frameRadius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - frameRadius);
      ctx.lineTo(x, y + frameRadius);
      ctx.quadraticCurveTo(x, y, x + frameRadius, y);
      ctx.closePath();
      ctx.stroke();
    }

    // Draw QR code
    const qrOffset = offset + framePadding + frameStroke;
    ctx.drawImage(canvas, horizontalOffset + qrOffset, qrOffset + verticalOffset, size, size);

    // Draw hearts if enabled - use brighter pink (#F472B6)
    if (showHearts) {
      ctx.fillStyle = '#F472B6'; // Brighter pink (Tailwind pink-400)
      for (const pos of heartPositions) {
        ctx.save();
        const heartX = horizontalOffset + offset + pos.x;
        const heartY = offset + pos.y + verticalOffset;
        ctx.translate(heartX + 12 * pos.scale, heartY + 12 * pos.scale);
        ctx.rotate((pos.rotation * Math.PI) / 180);
        ctx.scale(pos.scale, pos.scale);

        // Draw heart path
        ctx.beginPath();
        const heartPath = new Path2D('M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z');
        ctx.translate(-12, -12);
        ctx.fill(heartPath);
        ctx.restore();
      }
    }

    // Trigger download
    return new Promise<void>((resolve) => {
      exportCanvas.toBlob((blob) => {
        if (!blob) {
          resolve();
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    });
  }, [size, containerSize, totalSize, frameColor, frameStroke, framePadding, heartOverflow, showFrame, showHearts, heartPositions]);

  // Expose download method via ref
  useImperativeHandle(ref, () => ({
    download: handleDownload,
  }), [handleDownload]);

  return (
    <div
      className="relative inline-block"
      style={{
        width: containerSize,
        height: containerSize,
      }}
    >
      {/* Navy blue frame */}
      {showFrame && (
        <div
          className="absolute"
          style={{
            left: heartOverflow,
            top: heartOverflow,
            width: totalSize,
            height: totalSize,
            border: `${frameStroke}px solid ${frameColor}`,
            borderRadius: 12,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* QR Code container */}
      <div
        ref={containerRef}
        className="absolute bg-white"
        style={{
          left: heartOverflow + framePadding + frameStroke,
          top: heartOverflow + framePadding + frameStroke,
          width: size,
          height: size,
          borderRadius: 4,
        }}
      />

      {/* Decorative hearts - brighter pink */}
      {showHearts && heartPositions.map((pos, i) => (
        <HeartIcon
          key={i}
          style={{
            position: 'absolute',
            left: heartOverflow + pos.x,
            top: heartOverflow + pos.y,
            width: 24 * pos.scale,
            height: 24 * pos.scale,
            transform: `rotate(${pos.rotation}deg)`,
            color: '#F472B6', // Brighter pink (Tailwind pink-400)
          }}
        />
      ))}
    </div>
  );
});

StyledQRCode.displayName = 'StyledQRCode';

export default StyledQRCode;
